import React from "react";

import { setFootprintingCropPrimaryDataAdmin, setFootprintingFarmPrimaryDataAdmin } from "services";

import {
  getYearArray,
  isPercentageComplete,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingCropFootprints,
  useGetFootprintingFarmFootprints,
  useGetPracticeCrops,
  useNavigate,
} from "utils";

import { Button, Flex, PageSpin, Table, Tag } from "components";

interface FootprintingVerificationsAssessmentListProps {
  farmId: string;
}

const FootprintingVerificationsAssessmentList = ({ farmId }: FootprintingVerificationsAssessmentListProps) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const { data: crops } = useGetPracticeCrops({ isAdmin: true });
  const { data: carbonProgram } = useGetCarbonProgramByFarmId(farmId);

  const availableYears = getYearArray(carbonProgram?.farmLevelFootprinting?.startYear ?? currentYear, currentYear);
  const farmProgressQueries = useGetFootprintingFarmFootprints(farmId, availableYears);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, availableYears, carbonProgram?.farmLevelFootprinting?.programCrops);

  const allQueries = [...cropProgressQueries, ...farmProgressQueries];
  const isLoading = allQueries.some(queryIsLoading);
  if (isLoading) return <PageSpin />;

  const columns = [
    { title: "Year", dataIndex: "year" },
    { title: "Assessment", dataIndex: "assessment" },
    { title: "Percentage Complete", dataIndex: "completionPct" },
    { title: "Submitted", dataIndex: "isSubmitted" },
    { title: "Verified", dataIndex: "isVerified" },
    { title: "Actions", dataIndex: "actions" },
  ];

  const updateFootprintVerifiedStatus = (primaryDataId: string, farmId: string, year: number, isVerified: boolean, cropId?: string) => {
    if (cropId) {
      cropProgressQueries.map((q) => q.refetch());
      setFootprintingCropPrimaryDataAdmin({ isVerified, farmId, year, cropId }, primaryDataId).then(() => {
        cropProgressQueries.map((q) => q.refetch());
      });
    } else {
      farmProgressQueries.map((q) => q.refetch());
      setFootprintingFarmPrimaryDataAdmin({ isVerified, farmId, year }, primaryDataId).then(() => {
        farmProgressQueries.map((q) => q.refetch());
      });
    }
  };

  const data = allQueries
    .map(({ data }) => {
      if (!data || !data.footprint?.id) return null;

      // @ts-expect-error I know that only crop FPs have cropId, that's the point
      const cropId = data.cropId ?? null;

      return {
        key: data.footprint?.id,
        sortValue: data.year - (cropId !== null ? 0.1 : 0),
        year: data.year,
        assessment: `${cropId ? crops?.find((c) => c.id === cropId)?.name : "Farm"}`,
        completionPct: `${(data.completionPct * 100).toFixed(0)}%`,
        isSubmitted: data.footprint?.isSubmitted ? <Tag text="Submitted" colour="green-200" /> : <Tag text="Not submitted" colour="orange" />,
        isVerified: data.footprint?.isVerified ? <Tag text="Verified" colour="green-200" /> : <Tag text="Not verified" colour="orange" />,
        actions: (
          <Flex gap="1rem">
            <Button
              style={{ width: "fit-content" }}
              type="primary"
              disabled={!isPercentageComplete(data.completionPct)}
              onClick={() => updateFootprintVerifiedStatus(data.footprint!.id, farmId, data.year, !data.footprint?.isVerified, cropId)}
            >
              {data.footprint?.isVerified === true ? "Unverify" : "Mark Verified"}
            </Button>
            <Button
              style={{ width: "fit-content" }}
              type="secondary"
              onClick={() => navigate(`/verifications/farms/${farmId}/footprinting/${cropId ? "crop" : "farm"}/${data.footprint?.id}`)}
            >
              See Details
            </Button>
          </Flex>
        ),
      };
    })
    .filter((row) => !!row)
    .sort((a, b) => b.sortValue - a.sortValue);

  return <Table data={data} columns={columns} rowKey={(r) => r.key} />;
};

export default FootprintingVerificationsAssessmentList;
